import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Custom element wrapping a back-in-stock email capture form.
 *
 * Stays hidden by default and is only revealed when Swym's notify-me UI
 * fails to render in the DOM within ~3s AND the current variant is
 * unavailable. Detection uses a MutationObserver looking for any
 * Swym-injected element (class or id starting with `swym-`); checking
 * `window._swat` alone is not sufficient because Firefox/Edge tracker
 * prevention can let the script load but block the cookies/storage Swym
 * needs to render UI.
 *
 * If Swym appears later than 3s, the observer continues watching and
 * re-hides the form to avoid showing two notify-me UIs.
 *
 * Listens for the `shopify:product:select` standard event on the closest section
 * and, once its promise resolves, refreshes data attributes / toggles visibility.
 * Horizon 4.x removed `ThemeEvents.variantUpdate`; this follows
 * assets/product-inventory.js and assets/pricing-blocks.js (WHHA-1046).
 *
 * @typedef {Object} BackInStockFormRefs
 * @property {HTMLInputElement} email
 * @property {HTMLButtonElement} submit
 * @property {HTMLParagraphElement} message
 *
 * @extends {Component<BackInStockFormRefs>}
 */
class BackInStockForm extends Component {
  requiredRefs = ['email', 'submit', 'message'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #revealTimer;

  /** @type {MutationObserver | undefined} */
  #swymObserver;

  /** @type {'pending' | 'loaded' | 'failed'} */
  #swymStatus = 'pending';

  connectedCallback() {
    super.connectedCallback();

    this.#removeStaleSiblings();

    this.#abortController = new AbortController();
    const section = this.closest('.shopify-section, dialog') || this.closest('section');
    section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal: this.#abortController.signal }
    );

    this.#waitForSwymUI();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    clearTimeout(this.#revealTimer);
    this.#swymObserver?.disconnect();
  }

  #removeStaleSiblings() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const all = document.querySelectorAll(
      `back-in-stock-form[data-product-id="${CSS.escape(productId)}"]`
    );
    for (const el of all) {
      if (el !== this) el.remove();
    }
  }

  notify(event) {
    event?.preventDefault();

    const emailField = this.refs.email;
    if (!emailField.checkValidity()) {
      emailField.reportValidity();
      return;
    }

    const email = emailField.value.trim();
    if (!email) return;

    const swat = window._swat;
    if (typeof swat?.sendWatchlist !== 'function') {
      this.#disableWith(this.dataset.textUnavailable, 'unavailable');
      return;
    }

    this.refs.submit.disabled = true;

    try {
      swat.sendWatchlist(
        email,
        'email',
        {
          epi: Number(this.dataset.variantId),
          empi: Number(this.dataset.productId),
          iu: this.dataset.productImage,
          stk: Number(this.dataset.inventoryQty),
          pr: Number(this.dataset.productPrice) / 100,
          et: 8,
        },
        () => {
          this.refs.email.disabled = true;
          this.refs.submit.disabled = true;
          this.#showMessage(this.dataset.textSuccess, 'success');
        },
        (err) => {
          this.refs.submit.disabled = false;
          const text = (err && err.message) || this.dataset.textFailed;
          this.#showMessage(text, 'error');
        },
        1
      );
    } catch (err) {
      console.warn('[back-in-stock] sendWatchlist error:', err);
      this.#disableWith(this.dataset.textUnavailable, 'unavailable');
    }
  }

  /** @param {any} event - The `shopify:product:select` event. */
  #handleProductSelect = (event) => {
    // Ignore selections made on a product card, unless this form is inside that card.
    const sourceCard = event.target instanceof Element ? event.target.closest('product-card') : null;
    if (sourceCard && !sourceCard.contains(this)) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        this.#applyVariant(detail.resource);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[back-in-stock] Event promise rejected:', error);
      });
  };

  #applyVariant(variant) {
    // No variant for this selection (resource is null, e.g. an unavailable option
    // combination or a B2B context where selected_or_first_available_variant is nil):
    // there is nothing to notify about, and the stale data-variant-id must not take a
    // signup. 'none' also keeps the Swym reveal timer from re-showing it (WHHA-1046).
    if (!variant) {
      this.dataset.variantAvailable = 'none';
      this.hidden = true;
      return;
    }

    this.dataset.variantAvailable = String(variant.available);

    if (variant.available) {
      this.hidden = true;
      return;
    }

    this.dataset.variantId = variant.id;
    this.dataset.inventoryQty = variant.inventory_quantity ?? 0;
    this.dataset.productPrice = variant.price;
    if (variant.featured_image?.src) {
      this.dataset.productImage = variant.featured_image.src;
    }

    if (this.#swymStatus === 'failed') {
      this.hidden = false;
    }
  }

  #waitForSwymUI() {
    if (this.#findSwymUI()) {
      this.#swymStatus = 'loaded';
      return;
    }

    this.#swymObserver = new MutationObserver(() => {
      if (this.#findSwymUI()) {
        this.#swymStatus = 'loaded';
        this.hidden = true;
        clearTimeout(this.#revealTimer);
        this.#revealTimer = undefined;
        this.#swymObserver?.disconnect();
        this.#swymObserver = undefined;
      }
    });
    this.#swymObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.#revealTimer = setTimeout(() => {
      this.#revealTimer = undefined;
      if (this.#swymStatus === 'pending') {
        this.#swymStatus = 'failed';
        this.#revealAsFailed();
        document.dispatchEvent(
          new CustomEvent('back-in-stock:swym-ui-missing', { bubbles: true })
        );
      }
    }, 3000);
  }

  #findSwymUI() {
    return document.querySelector('[class*="swym-isa"]');
  }

  #revealAsFailed() {
    // Reveal only for a variant that exists and is unavailable: 'true' is purchasable,
    // 'none' means the selection has no variant at all (WHHA-1046).
    if (this.dataset.variantAvailable !== 'false') return;
    this.hidden = false;
  }

  /**
   * @param {string | undefined} text
   * @param {string} state
   */
  #disableWith(text, state) {
    this.refs.submit.disabled = true;
    this.#showMessage(text, state);
  }

  /**
   * @param {string | undefined} text
   * @param {string} state
   */
  #showMessage(text, state) {
    const el = this.refs.message;
    el.textContent = text ?? '';
    el.dataset.state = state;
    el.hidden = false;
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
